import React, { useMemo } from 'react';
import OrderStatus from 'components/base/order-status';
import OrderTag from 'components/base/order-tag';
import { Dropdown } from '@cokitchen/cokitchen-components';
import { ReactComponent as MenuIcon } from 'assets/icons/menu.svg';
import { ENUM_STATUS } from 'utils/enum';
import RecallOrderTag from 'components/recall-order-tag';
import { getKitchenOrderStatus, getOrderStatus, getUserName } from 'utils/functions';
import { OrderType } from 'models/order.model';
import OrderTime from '../order-time';
import UnpaidOrderTimer from '../unpaid-order-timer';
import { getCokitchenTeam } from 'utils/role-config';
import { ENUM_COKITCHEN_TEAM } from '@cokitchen/cokitchen-auth';
import { ENUM_LIVE_CARD_MENU, LiveCardListType } from '../menu-config';
import { withDropdownList } from 'components/base/dropdown-list';
import { getLiveCardMenuList } from '../util';
import OrderDeliveryProvider from 'components/base/order-delivery-provider';

type Props = {
  ridersAvailable: boolean;
  onSelectMenu: (menuKey: ENUM_LIVE_CARD_MENU) => void;
  order: OrderType;
};

const DropdownList = withDropdownList<LiveCardListType>();

const CSLiveTripCardHeader = ({ order, ridersAvailable, onSelectMenu }: Props) => {
  const currentTeam = getCokitchenTeam();
  const surge = order.surge?.logistics_surge && order.surge?.logistics_surge !== '1.0';
  const isRecalled =
    order?.order_logs?.find((log) => log.action === 'order_recalled') &&
    getOrderStatus(order as OrderType) !== ENUM_STATUS.RECALLED;

  const type = useMemo(() => {
    return currentTeam === ENUM_COKITCHEN_TEAM.KITCHEN
      ? getKitchenOrderStatus(order)
      : getOrderStatus(order);
  }, [order, currentTeam]);

  const late = useMemo(() => {
    return (
      order.paid &&
      (order.kitchen_time_elapsed ||
        (order?.estimated_delivery_time
          ? new Date(order.estimated_delivery_time).getTime() < new Date().getTime()
          : false))
    );
  }, [order]);

  const cs_rep = useMemo(() => {
    return getUserName(order.cs_rep?.user, 'No rep assigned');
  }, [order.cs_rep]);

  const list = useMemo(() => {
    return getLiveCardMenuList(order, ridersAvailable);
  }, [order, ridersAvailable]);

  return (
    <>
      <div className='flex border-b border-[#ECEDEF] pb-2 items-center gap-1 flex-wrap justify-between px-3'>
        <div className='flex flex-col gap-2 items-start'>
          <div className='flex gap-2 items-center'>
            <OrderStatus type={type} />
            {late && <OrderTag type='late' />}
            {surge && <OrderTag type='surge' />}
            {isRecalled && <RecallOrderTag />}
            {type === ENUM_STATUS.UNPAID && <UnpaidOrderTimer order={order} />}
          </div>
          <div className='flex gap-2'>
            <OrderDeliveryProvider order={order} />
            <h4 className='font-medium text-base cursor-pointer'>
              #{order.order_code?.toUpperCase()}
            </h4>
          </div>
        </div>
        <div className='flex flex-col gap-1 items-end'>
          <label className='text-[#3D3D3D] text-sm font-medium'>{cs_rep}</label>
          <div className='flex gap-1 items-center'>
            {<OrderTime order={order} />}
            {type !== 'complete' && list.length > 0 && (
              <Dropdown
                content={
                  <MenuIcon
                    onClick={(e) => e.stopPropagation()}
                    className='text-[#95979D] transition-all duration-200 w-6 h-6  p-1 hover:text-gray-6 cursor-pointer'
                  />
                }>
                <DropdownList list={list} onSelect={(item) => onSelectMenu(item.key)} />
              </Dropdown>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CSLiveTripCardHeader;
